'use client';

import React from 'react';
import {
  BracketsCurlyIcon,
  FileAudioIcon,
  FileHtmlIcon,
  FilePdfIcon,
  FileTextIcon,
  PlayIcon,
  TableIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { MediaCard } from '@tranquility-ai/theme-components/media-card';
import { MediaSource } from '@/shared/types';
import { getMediaSourceListRowKey } from '@/shared/clientFunctions';
import { mediaCaptionPath, explicitCitationPageFromMedia } from '@/shared/functions';
import { getCompactPreviewDisplayUrl } from '@/shared/timelineThumbnails';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { STACKED_DISPLAYED_MEDIA_STRIP_CLASS } from './sourcesSection';

/**
 * File name of the media the card previews.
 *
 * Reads the canonical source path rather than ``mediaUrl``: the latter names the
 * rendition on screen, so a moderated image would caption the card
 * ``…_obfuscated.jpg`` and a PDF its markdown conversion (TR-2153).
 */
function mediaCardLabel(media: MediaSource): string {
  const basename = mediaCaptionPath(media).split('/').pop() || '';
  const page = explicitCitationPageFromMedia(media);
  if (page === undefined) return basename;
  return `${basename} — p. ${page}`;
}

function CompactMediaTypeIcon({ mediaType }: { mediaType?: string }) {
  const className = 'text-white/80';
  const size = 32;
  switch (mediaType) {
    case 'audio':
      return <FileAudioIcon size={size} className={className} />;
    case 'video':
      return <PlayIcon size={size} weight="fill" className={className} />;
    case 'pdf':
    case 'docx':
    case 'pptx':
      return <FilePdfIcon size={size} className={className} />;
    case 'spreadsheet':
      return <TableIcon size={size} weight="light" className={className} />;
    case 'json':
      return <BracketsCurlyIcon size={size} className={className} />;
    case 'html':
      return <FileHtmlIcon size={size} className={className} />;
    case 'markdown':
    case 'text':
      return <FileTextIcon size={size} className={className} />;
    default:
      return <FileTextIcon size={size} className={className} />;
  }
}

type DisplayedMediaStripProps = {
  /** Display-resolved media rows, index-aligned with the parent media array. */
  media: MediaSource[];
  selectedIndex: number;
  editingEnabled: boolean;
  caseId?: string;
  jsonMediaDisplayEnabled: boolean;
  /** Thumbnails are suppressed while the app fullscreen viewer owns the media. */
  isFullscreen: boolean;
  onSelect: (index: number) => void;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
};

/**
 * Grid of Displayed Media cards. Each card previews the media the same way it
 * renders in slide/presentation view. Clicking the tile opens the fullscreen
 * viewer; a persistent secondary remove control sits on the tile. Cards wrap to
 * new rows when the container is narrower than a single horizontal row.
 */
export function DisplayedMediaStrip({
  media,
  selectedIndex,
  editingEnabled,
  isFullscreen,
  onSelect,
  onEdit,
  onRemove,
}: DisplayedMediaStripProps) {
  const t = useTimePilotT();
  return (
    <div className={STACKED_DISPLAYED_MEDIA_STRIP_CLASS}>
      {media.map((item, index) => {
        const captionPath = mediaCaptionPath(item);
        const label = mediaCardLabel(item);
        const hasPlayableSource =
          !!item.mediaSrc ||
          !!item.mediaUrl ||
          (item.mediaType === 'json' && !!item.content?.trim());
        const isSelected = selectedIndex === index;
        const previewSrc = getCompactPreviewDisplayUrl(item);
        const hasPreviewImage = Boolean(previewSrc);
        const showTypeIconFallback = hasPlayableSource && !hasPreviewImage && item.mediaType !== 'youtube';
        const showAddThumbnailPrompt =
          showTypeIconFallback && !isFullscreen && item.mediaType !== 'audio';
        const showAudioIconOnly =
          showTypeIconFallback && !isFullscreen && item.mediaType === 'audio';

        const youtubeThumb =
          item.mediaType === 'youtube' && !previewSrc && (item.mediaSrc || item.mediaUrl)
            ? `https://img.youtube.com/vi/${
                item.mediaSrc?.split('/').pop() || item.mediaUrl?.split('/').pop()
              }/0.jpg`
            : undefined;

        let placeholder: React.ReactNode;
        if (!hasPlayableSource) {
          placeholder = (
            <span className="line-clamp-3 overflow-hidden text-ellipsis p-2 text-center text-[12px] text-[color:var(--text-muted)]">
              {t('sources.noMedia')}
            </span>
          );
        } else if (showAddThumbnailPrompt) {
          placeholder = (
            <div
              className="flex size-full flex-col items-center justify-center gap-2 px-3"
              data-testid="displayed-media-add-thumbnail"
            >
              <CompactMediaTypeIcon mediaType={item.mediaType} />
              <span className="text-center text-[11px] leading-4 text-white/80">
                {t('media.addPreview')}
              </span>
            </div>
          );
        } else if (showAudioIconOnly) {
          placeholder = (
            <div
              className="flex size-full items-center justify-center"
              data-testid="displayed-media-audio-icon"
            >
              <CompactMediaTypeIcon mediaType={item.mediaType} />
            </div>
          );
        } else if (!isFullscreen && previewSrc) {
          placeholder = (
            <img
              src={previewSrc}
              alt=""
              data-testid="displayed-media-thumbnail"
            />
          );
        } else if (youtubeThumb) {
          placeholder = <img src={youtubeThumb} alt="" />;
        }

        return (
          <MediaCard
            key={getMediaSourceListRowKey(item, index)}
            data-testid="displayed-media-card"
            className="shrink-0"
            tone="dark"
            label={label}
            title={captionPath || label}
            mediaType={item.mediaType}
            selected={isSelected}
            placeholder={placeholder}
            aria-label={
              editingEnabled
                ? item.mediaType === 'audio'
                  ? t('sources.openDisplayed', { label })
                  : t('sources.addPreviewFor', { label })
                : t('sources.selectDisplayed', { label })
            }
            frameProps={
              editingEnabled ? { 'data-testid': 'displayed-media-edit' } : undefined
            }
            onSelect={() => {
              if (editingEnabled) {
                onEdit(index);
              } else {
                onSelect(index);
              }
            }}
            action={
              editingEnabled ? (
                <Button
                  type="button"
                  iconOnly
                  size="sm"
                  style="solid"
                  color="secondary"
                  background="inverse"
                  aria-label={t('sources.removeDisplayed', { label })}
                  data-testid="displayed-media-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(index);
                  }}
                >
                  <XIcon />
                </Button>
              ) : undefined
            }
          />
        );
      })}
    </div>
  );
}
